import { type ReactNode } from 'react'

import { Descriptions, Header, InfoBadge, Space } from '@procurify/ui'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { Price } from '@webapp/core/components/Price'
import { useFormatPrice, useProcurifyIntl } from '@webapp/hooks'
import { ExpenseTypes } from '@webapp/modules/expense/entities'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { useFormatExpenseItemTypeLabel } from '@webapp/modules/expense/hooks/useFormatExpenseItemTypeLabel'
import messages from './messages'

export interface IExpenseTypeSummaryProps {
  item: ExpenseItem
}

export const ExpenseTypeSummary = ({ item }: IExpenseTypeSummaryProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()
  const formatPrice = useFormatPrice()
  const formatExpenseItemTypeLabel = useFormatExpenseItemTypeLabel()

  const getHeaderText = (): string => {
    if (item.expenseType === ExpenseTypes.OTHER) {
      return intl.formatMessage(messages.itemLabel)
    } else {
      const expenseType = formatExpenseItemTypeLabel(item.expenseType)
      return intl.formatMessage(messages.itemWithTypeLabel, { expenseType })
    }
  }

  const getExpenseTypeDataFields = (): {
    label: ReactNode
    value: ReactNode
  }[] => {
    switch (item.expenseTypeData.type) {
      case 'AIRFARE':
        return [
          {
            label: intl.formatMessage(messages.ticketNumberLabel),
            value: item.expenseTypeData.data.ticketNumber,
          },
          {
            label: intl.formatMessage(messages.departureDateLabel),
            value: item.expenseTypeData.data.departureDate
              ? formatDate(item.expenseTypeData.data.departureDate)
              : null,
          },
          {
            label: intl.formatMessage(messages.arrivalDateLabel),
            value: item.expenseTypeData.data.arrivalDate
              ? formatDate(item.expenseTypeData.data.arrivalDate)
              : null,
          },
          {
            label: intl.formatMessage(messages.airlineLabel),
            value: item.expenseTypeData.data.airline,
          },
        ]
      case 'LABOUR':
        return [
          {
            label: intl.formatMessage(messages.hourlyRateLabel),
            value: `${formatPrice(item.expenseTypeData.data.hourlyRate)} ${item.currency.name}`,
          },
          {
            label: intl.formatMessage(messages.hoursOfWorkLabel),
            value: item.expenseTypeData.data.hoursOfWork,
          },
        ]
      case 'MEALS':
        return [
          {
            label: intl.formatMessage(messages.tipsLabel),
            value: `${formatPrice(item.expenseTypeData.data.tips)} ${item.currency.name}`,
          },
        ]
      case 'MILEAGE':
        return [
          {
            label: (
              <Space direction='horizontal' size='small'>
                <>{intl.formatMessage(messages.mileageRateLabel)}</>
                {!item.mileageRatePresetId && (
                  <InfoBadge
                    type='neutral'
                    text={intl.formatMessage(messages.customRateLabel)}
                    size='small'
                    fontSize={12}
                  />
                )}
              </Space>
            ),
            value: (
              <Space>
                <Space direction='horizontal' justify='space-between'>
                  {!!item.mileageRatePresetName && (
                    <>{item.mileageRatePresetName}</>
                  )}
                  <>
                    {`${formatPrice(item.expenseTypeData.data.mileageRate)} ${item.currency.name}/${item.expenseTypeData.data.unitOfDistance}`}
                  </>
                </Space>
                {item.expenseTypeData.data.advisoryFuelRate && (
                  <Space direction='horizontal' justify='space-between'>
                    {!!item.mileageRatePresetName && (
                      <>{intl.formatMessage(messages.advisoryFuelRateLabel)}</>
                    )}
                    <>
                      {`${formatPrice(item.expenseTypeData.data.advisoryFuelRate)} ${item.currency.name}/${item.expenseTypeData.data.unitOfDistance}`}
                    </>
                  </Space>
                )}
              </Space>
            ),
          },
          {
            label: intl.formatMessage(messages.distanceLabel),
            value: `${item.expenseTypeData.data.distance} ${item.expenseTypeData.data.unitOfDistance}`,
          },
        ]
      case 'LODGING':
      default:
        return []
    }
  }

  const fields = getExpenseTypeDataFields()

  return (
    <Space size='small' align='stretch'>
      <Header as='h5'>{getHeaderText()}</Header>

      {fields.length > 0 && (
        <Descriptions size='small' valueAlign='right' valueWidth='fill'>
          {fields.map((field) => (
            <Descriptions.Item key={String(field.label)} label={field.label}>
              {field.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {fields.length > 0 && <Space.Divider />}

      <Descriptions size='small' valueAlign='right' valueWidth='fill'>
        <Descriptions.Item label={intl.formatMessage(messages.subtotalLabel)}>
          <Price price={item.subtotal} currency={item.currency.name} />
        </Descriptions.Item>
      </Descriptions>
    </Space>
  )
}
